import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section, MetricsGrid } from '@dized/ui';
import { EnhancedQuickStats } from './EnhancedQuickStats';
import { MetricCard } from '../MetricCard.js';

// Enhanced time formatting function with configurable precision
const formatTime = (seconds, maxUnits = 2) => {
  if (!seconds && seconds !== 0) return '0s';
  
  const totalSeconds = Math.round(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  const units = [];
  if (days > 0) units.push(`${days}d`);
  if (hours > 0) units.push(`${hours}h`);
  if (minutes > 0) units.push(`${minutes}m`);
  if (secs > 0) units.push(`${secs}s`);
  
  if (units.length === 0) return '0s';
  
  // Return specified number of most significant units
  return units.slice(0, maxUnits).join(' ');
};

const formatTimeDetailed = (seconds) => {
  if (!seconds && seconds !== 0) return '0 seconds';
  
  const totalSeconds = Math.round(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  const parts = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
  if (secs > 0) parts.push(`${secs} second${secs > 1 ? 's' : ''}`);
  
  return parts.length > 0 ? parts.join(' ') : '0 seconds';
};

// Helper functions for metric-specific calculations and formatting
const formatNumber = (value) => {
  if (!value && value !== 0) return '0';
  
  // Handle decimal numbers (for averages)
  if (value % 1 !== 0) {
    return parseFloat(value.toFixed(1)).toString();
  }
  
  // Handle whole numbers with commas for large values
  return Math.floor(value).toLocaleString();
};

const getAdditionalInfo = (label, data) => {
  if (!data) return '';
  
  // Helper to create consistent additional info messages
  const createSessionInfo = (count) => count ? `Based on ${formatNumber(count)} sessions` : '';
  const createEventInfo = (count) => count ? `Based on ${formatNumber(count)} player events` : '';
  const createDurationInfo = (count) => count ? `Across ${formatNumber(count)} session durations` : '';
  
  // Mapping object for cleaner management
  const infoConfig = {
    'Total Players': () => {
      // Ensure we use the aggregated total_player_count_based_on
      const eventCount = data.total_player_count_based_on || 'Unknown';
      const baseInfo = eventCount ? createEventInfo(eventCount) : 'Across all game instances and locales';
      return `Total unique players<br/>${baseInfo}`;
    },
    'Total Sessions': () => 'Individual game sessions<br/>Across all game instances and locales',
    'Avg Usage Time': () => {
      const sessionInfo = createSessionInfo(data.session_count);
      return sessionInfo ? `Average session duration<br/>${sessionInfo}` : 'Average session duration';
    },
    'Median Time': () => {
      const durationInfo = createDurationInfo(data.session_count);
      return durationInfo ? `Middle value of durations<br/>${durationInfo}` : 'Middle value of durations';
    },
    'Total Blocks Advanced': () => {
      const sessionInfo = createSessionInfo(data.session_count);
      return sessionInfo ? `Code blocks completed<br/>${sessionInfo}` : 'Code blocks completed';
    },
    'Total Time Played': () => {
      const timeStr = formatTimeDetailed(data.total_time_played);
      const sessionInfo = createSessionInfo(data.session_count);
      return sessionInfo ? `${timeStr} <br/> ${sessionInfo}` : timeStr;
    },
    'Avg Players/Session': () => {
      const eventCount = data.total_player_count_based_on;
      const baseInfo = eventCount ? createEventInfo(eventCount) : createSessionInfo(data.session_count);
      return `Average players per session<br/>${baseInfo}`;
    },
    'Avg Blocks/Session': () => {
      const sessionInfo = createSessionInfo(data.session_count);
      return sessionInfo ? `Average blocks per session<br/>${sessionInfo}` : 'Average blocks per session';
    },
    'Avg Time/Session': () => {
      const durationInfo = createDurationInfo(data.session_count);
      return durationInfo ? `Average session duration<br/>${durationInfo}` : 'Average session duration';
    }
  };
  
  // Get the info function for this label, or return empty string
  const infoFunction = infoConfig[label];
  return infoFunction ? infoFunction() : '';
};

const getMetricExplanation = (label) => {
  const explanations = {
    'Total Players': 'Number of players who have played this game. This is asked during the tutorial setup. User must have reached to the point where this is asked.',
    'Total Sessions': `Individual tutorial sessions played. Each time someone starts the tutorial, it counts as one session. If tutorial crashed or was continued later, it doesn't count as new session.`,
    'Avg Usage Time': 'Average time players spend per session. Helps understand typical session length and engagement.',
    'Median Time': 'Middle value of all session durations when sorted. Better represents typical play time than average.',
    'Total Blocks Advanced': 'Total blocks successfully completed by all players across all sessions. Only unique blocks are counted. Going back to previous blocks does not count as new.',
    'Total Time Played': 'Sum of all time spent by all players across all sessions. Total engagement metric.',
    'Avg Players/Session': 'Average number of players per session. This metric is calculated from the total sessions where players count was set.',
    'Avg Blocks/Session': 'Average blocks completed per session.',
    'Avg Time/Session': 'Average session duration. Shows how long players typically engage in one sitting.'
  };
  
  return explanations[label] || 'Detailed information about this metric is not available.';
};

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#2196F3' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#1976D2' : '#f5f5f5'};
  }
`;

export const QuickStats = ({ data }) => {
  const [viewMode, setViewMode] = useState('cards');
  
  if (!data) {
    return (
      <Section>
        <ResponsiveTitle>Quick Stats</ResponsiveTitle>
        <p>No data available</p>
      </Section>
    );
  }

  // Debug log to check data structure
  console.log('QuickStats data:', {
    total_player_count_based_on: data.total_player_count_based_on,
    session_count: data.session_count,
    total_player_count: data.total_player_count
  });

  // Calculate comprehensive metrics using ALL available fields
  const totalPlayers = data.total_player_count || 0; // Sum of total_player_count from all locale responses
  const totalSessions = data.session_count || 0; // Sum of session_count from all locale responses
  const avgUsageTime = Math.round(data.avg_time_played || 0); // Use server-provided avg_time_played averaged across locales
  const medianUsageTime = Math.round(data.median_time_played || 0); // Use server-provided median_time_played averaged across locales
  const totalBlocksAdvanced = data.total_blocks_advanced || 0; // Sum of total_blocks_advanced from all locale responses
  const totalTimePlayed = data.total_time_played || 0; // Sum of total_time_played from all locale responses
  
  // Calculate avg players per session using "Set Player" events for accuracy
  const calculateAvgPlayersPerSession = () => {
    // Use the "Set Player" events field for accurate calculation
    if (!data.total_player_count_based_on || data.total_player_count_based_on === 0) {
      return 0;
    }
    
    const avgPlayers = totalPlayers / data.total_player_count_based_on;
    return Math.floor(avgPlayers * 10) / 10; // Round down to 1 decimal place
  };
  
  const avgPlayersPerSession = data.avg_players_per_session 
    ? Math.floor(data.avg_players_per_session * 10) / 10 
    : calculateAvgPlayersPerSession();

  return (
    <Section>
      <ResponsiveTitle>Quick Stats</ResponsiveTitle>
      
      <ViewToggle>
        <ToggleButton 
          active={viewMode === 'cards'} 
          onClick={() => setViewMode('cards')}
        >
          📊 Metric Cards
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'enhanced'} 
          onClick={() => setViewMode('enhanced')}
        >
          📈 Enhanced Charts
        </ToggleButton>
      </ViewToggle>

      {viewMode === 'cards' ? (
        <MetricsGrid>
          <MetricCard
            value={totalPlayers}
            label="Total Players"
            color="#4CAF50"
            tooltip={getMetricExplanation('Total Players')}
            additionalInfo={getAdditionalInfo('Total Players', data)}
          />
          <MetricCard
            value={totalSessions}
            label="Total Sessions"
            color="#2196F3"
            tooltip={getMetricExplanation('Total Sessions')}
            additionalInfo={getAdditionalInfo('Total Sessions', data)}
          />
          <MetricCard
            value={avgUsageTime}
            label="Avg Usage Time"
            format="time"
            color="#FF9800"
            tooltip={getMetricExplanation('Avg Usage Time')}
            additionalInfo={getAdditionalInfo('Avg Usage Time', data)}
            displayValueFormatter={(value) => formatTime(value, 2)}
          />
          <MetricCard
            value={medianUsageTime}
            label="Median Time"
            format="time"
            color="#9C27B0"
            tooltip={getMetricExplanation('Median Time')}
            additionalInfo={getAdditionalInfo('Median Time', data)}
            displayValueFormatter={(value) => formatTime(value, 2)}
          />
          <MetricCard
            value={totalBlocksAdvanced}
            label="Total Blocks Advanced"
            color="#F44336"
            tooltip={getMetricExplanation('Total Blocks Advanced')}
            additionalInfo={getAdditionalInfo('Total Blocks Advanced', data)}
          />
          <MetricCard
            value={totalTimePlayed}
            label="Total Time Played"
            format="time"
            color="#00BCD4"
            tooltip={getMetricExplanation('Total Time Played')}
            additionalInfo={getAdditionalInfo('Total Time Played', data)}
            displayValueFormatter={(value) => formatTime(value, 2)}
          />
          <MetricCard
            value={avgPlayersPerSession}
            label="Avg Players/Session"
            format="number"
            color="#E91E63"
            tooltip={getMetricExplanation('Avg Players/Session')}
            additionalInfo={getAdditionalInfo('Avg Players/Session', data)}
          />
          <MetricCard
            value={Math.round(data.avg_blocks_advanced_per_session || 0)}
            label="Avg Blocks/Session"
            format="number"
            color="#795548"
            tooltip={getMetricExplanation('Avg Blocks/Session')}
            additionalInfo={getAdditionalInfo('Avg Blocks/Session', data)}
          />
          <MetricCard
            value={Math.round(data.avg_time_per_session || 0)}
            label="Avg Time/Session"
            format="time"
            color="#3F51B5"
            tooltip={getMetricExplanation('Avg Time/Session')}
            additionalInfo={getAdditionalInfo('Avg Time/Session', data)}
            displayValueFormatter={(value) => formatTime(value, 2)}
          />
        </MetricsGrid>
      ) : (
        <EnhancedQuickStats data={data} />
      )}
    </Section>
  );
};

QuickStats.propTypes = {
  data: PropTypes.shape({
    total_player_count: PropTypes.number,
    total_player_count_based_on: PropTypes.number, // Aggregated count from "Set Player" events
    session_count: PropTypes.number,
    avg_time_played: PropTypes.number,
    median_time_played: PropTypes.number,
    total_blocks_advanced: PropTypes.number,
    total_time_played: PropTypes.number,
    avg_blocks_advanced_per_session: PropTypes.number,
    avg_players_per_session: PropTypes.number,
    avg_time_per_session: PropTypes.number
  })
};

QuickStats.displayName = 'QuickStats';
